import React from "react";
import { formatModifierKey } from "../../../lib/platform";
import { shortcutSections } from "../../../lib/shortcuts";
import ShortcutDescription from "../../shortcuts/ShortcutDescription";
// Hidden until the underlying ⌘+/ overlay feature is fully shipped. Keep
// the import + usage commented (not deleted) — the banner's intro animation
// is intentional and worth preserving for when this re-enables.
// import ShortcutHintBanner from "../../shortcuts/ShortcutHintBanner";

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 bg-warm-bg-elevated border border-warm-border rounded text-xs font-mono text-warm-text-primary shadow-sm">
    {children}
  </kbd>
);

// Left padding for nested rows / sub-headings (level 2 sits deeper than 1).
const indentClass = (level?: number) =>
  level === 2 ? "pl-6" : level === 1 ? "pl-3" : "";

// kbd caps sit in a right-aligned auto column where padding gets absorbed, so
// shift them with a transform (matched to the label padding) instead.
const kbdIndentClass = (level?: number) =>
  level === 2 ? "translate-x-6" : level === 1 ? "translate-x-3" : "";

interface ShortcutsSettingsProps {
  /**
   * Hide the Email view section when Gmail isn't connected — those shortcuts
   * can't be triggered until the integration is enabled. Optional so the
   * component can be mounted in tests without wiring; defaults to "not
   * connected" (the safer assumption).
   */
  gmailConnected?: boolean;
}

const ShortcutsSettings: React.FC<ShortcutsSettingsProps> = ({
  gmailConnected = false,
}) => {
  const visibleSections = shortcutSections.filter(
    (s) => s.id !== "emails" || gmailConnected,
  );
  return (
    <div className="space-y-8">
      {/* <ShortcutHintBanner /> — hidden until ⌘+/ overlay ships */}

      {visibleSections.map((section) => (
        <section key={section.id}>
          <h3 className="font-heading text-base font-semibold text-warm-text-primary mb-3 pb-1.5 border-b border-warm-border">
            {section.title}
            {section.availabilityHint && (
              <span className="font-normal text-sm text-warm-text-tertiary">
                {" "}
                ({section.availabilityHint})
              </span>
            )}
          </h3>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-center">
            {section.shortcuts.map((shortcut, i) => {
              const next = section.shortcuts[i + 1];
              const closesSubgroup =
                shortcut.indentLevel && next && !next.indentLevel;
              const trailingSpaceClass = closesSubgroup ? "pb-3" : "";
              return (
                <React.Fragment key={i}>
                  {shortcut.subheadingBefore && (
                    <div
                      className={`col-span-2 text-xs font-bold uppercase tracking-wider text-warm-text-secondary pb-1 ${
                        i === 0 ? "" : "pt-3"
                      } ${indentClass(shortcut.subheadingIndentLevel)}`}
                    >
                      {shortcut.subheadingBefore}
                    </div>
                  )}
                  <div
                    className={`flex items-center gap-1 justify-end ${kbdIndentClass(
                      shortcut.indentLevel,
                    )} ${trailingSpaceClass}`}
                  >
                    {shortcut.keys.map((key, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && (
                          <span className="text-xs text-warm-text-tertiary">
                            {shortcut.chord ? "then" : "+"}
                          </span>
                        )}
                        <Kbd>{formatModifierKey(key)}</Kbd>
                      </React.Fragment>
                    ))}
                  </div>
                  <span
                    className={`text-sm text-warm-text-primary ${indentClass(
                      shortcut.indentLevel,
                    )} ${trailingSpaceClass}`}
                  >
                    <ShortcutDescription text={shortcut.description} />
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ShortcutsSettings;
